import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useCallback } from 'react';

import {
  Cryptocurrency,
  Currency,
  SupportedCurrency,
} from '__generated__/globalTypes';
import { useConfigContext } from 'contexts/config';
import { useCurrentUserContext } from 'contexts/currentUser';
import { withFragments } from 'lib/gql';
import { toLamport } from 'lib/lamport';
import type { MonetaryAmountParams } from 'lib/monetaryAmount';
import { MonetaryAmount } from 'lib/monetaryAmount';
import { toWei } from 'lib/wei';

import type { useMonetaryAmount_monetaryAmount } from './__generated__/useMonetaryAmount.graphql';

export const Sum = (amounts: MonetaryAmount[]) => {
  return amounts.reduce((acc, amount) => {
    return new MonetaryAmount({
      eurCents: acc.eurCents + amount.eurCents,
      usdCents: acc.usdCents + amount.usdCents,
      gbpCents: acc.gbpCents + amount.gbpCents,
      wei: acc.wei + amount.wei,
      lamport: acc.lamport + amount.lamport,
      referenceCurrency: acc.referenceCurrency,
    });
  }, MonetaryAmount.zero);
};

export const useMonetaryAmount = withFragments(
  () => {
    const { exchangeRate } = useConfigContext();
    const { currency, cryptocurrency, fiatCurrency } = useCurrentUserContext();

    const toMonetaryAmount = useCallback(
      (params: MonetaryAmountParams | null | undefined): MonetaryAmount => {
        if (params === null || params === undefined) {
          return MonetaryAmount.zero;
        }
        if (params instanceof MonetaryAmount) {
          return params;
        }
        return MonetaryAmount.fromRates(params, exchangeRate.ethRates);
      },
      [exchangeRate.ethRates]
    );

    const toMonetaryAmountInReferenceCurrency = useCallback(
      (
        amount: number | bigint | string,
        referenceCurrency: SupportedCurrency
      ): MonetaryAmount => {
        return MonetaryAmount.fromReferenceCurrency(
          referenceCurrency,
          amount,
          exchangeRate.ethRates
        );
      },
      [exchangeRate.ethRates]
    );

    const eur = useCallback(
      (amount: number): MonetaryAmount => {
        return toMonetaryAmountInReferenceCurrency(
          amount,
          SupportedCurrency.EUR
        );
      },
      [toMonetaryAmountInReferenceCurrency]
    );

    const usd = useCallback(
      (amount: number): MonetaryAmount => {
        return toMonetaryAmountInReferenceCurrency(
          amount,
          SupportedCurrency.USD
        );
      },
      [toMonetaryAmountInReferenceCurrency]
    );

    const gbp = useCallback(
      (amount: number): MonetaryAmount => {
        return toMonetaryAmountInReferenceCurrency(
          amount,
          SupportedCurrency.GBP
        );
      },
      [toMonetaryAmountInReferenceCurrency]
    );

    const wei = useCallback(
      (amount: bigint | number | string): MonetaryAmount => {
        return toMonetaryAmountInReferenceCurrency(
          BigInt(amount),
          SupportedCurrency.WEI
        );
      },
      [toMonetaryAmountInReferenceCurrency]
    );

    const lamport = useCallback(
      (amount: bigint | number | string): MonetaryAmount => {
        return toMonetaryAmountInReferenceCurrency(
          BigInt(amount),
          SupportedCurrency.LAMPORT
        );
      },
      [toMonetaryAmountInReferenceCurrency]
    );

    const toMonetaryAmountInFiatCurrency = useCallback(
      (amount: number): MonetaryAmount => {
        return toMonetaryAmountInReferenceCurrency(amount, fiatCurrency);
      },
      [toMonetaryAmountInReferenceCurrency, fiatCurrency]
    );

    const toMonetaryAmountFromMajorMainCurrency = useCallback(
      (amountInMajorUnit: number): MonetaryAmount => {
        if (currency === Currency.FIAT) {
          return toMonetaryAmountInReferenceCurrency(
            amountInMajorUnit * 100,
            fiatCurrency
          );
        }

        if (cryptocurrency === Cryptocurrency.LAMPORT) {
          return lamport(toLamport(amountInMajorUnit));
        }
        return wei(toWei(amountInMajorUnit));
      },
      [
        currency,
        cryptocurrency,
        wei,
        toMonetaryAmountInReferenceCurrency,
        fiatCurrency,
        lamport,
      ]
    );

    const getUserFiatAmountInMajorUnit = useCallback(
      (monetaryAmount: MonetaryAmount) =>
        monetaryAmount.toMajorUnit(fiatCurrency),
      [fiatCurrency]
    );

    const getUserCryptoAmountInSubUnit = useCallback(
      (monetaryAmount: MonetaryAmount) => {
        return monetaryAmount.cryptoSubUnit(cryptocurrency);
      },
      [cryptocurrency]
    );

    const getUserCryptoAmountInMajorUnit = useCallback(
      (monetaryAmount: MonetaryAmount) => {
        return monetaryAmount.crypto(cryptocurrency);
      },
      [cryptocurrency]
    );

    const getUserAmount = useCallback(
      (monetaryAmount: MonetaryAmount) => {
        if (currency === Currency.FIAT) {
          return getUserFiatAmountInMajorUnit(monetaryAmount);
        }
        return getUserCryptoAmountInMajorUnit(monetaryAmount);
      },
      [currency, getUserFiatAmountInMajorUnit, getUserCryptoAmountInMajorUnit]
    );

    return {
      toMonetaryAmount,
      toMonetaryAmountInFiatCurrency,
      toMonetaryAmountFromMajorMainCurrency,
      getUserFiatAmountInMajorUnit,
      getUserCryptoAmountInMajorUnit,
      getUserCryptoAmountInSubUnit,
      getUserAmount,
      toMonetaryAmountInReferenceCurrency,
      eur,
      usd,
      gbp,
      wei,
      lamport,
    };
  },
  {
    monetaryAmount: gql`
      fragment useMonetaryAmount_monetaryAmount on MonetaryAmount {
        eurCents
        usdCents
        gbpCents
        wei
        lamport
        referenceCurrency
      }
    ` as TypedDocumentNode<useMonetaryAmount_monetaryAmount>,
  }
);
